import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const navigation = [
    { 
      name: 'Dashboard', 
      href: '/dashboard', 
      icon: '🏠',
      description: 'Overview and quick actions'
    },
    { 
      name: 'Jobs', 
      href: '/jobs', 
      icon: '💼',
      description: 'Browse and manage job opportunities'
    },
    { 
      name: 'Swipe', 
      href: '/swipe', 
      icon: '👆', 
      roleRequired: 'TRADESPERSON',
      description: 'Discover new job opportunities'
    },
    { 
      name: 'Matches', 
      href: '/matches', 
      icon: '🤝',
      description: 'View your matched connections'
    },
    { 
      name: 'Messages', 
      href: '/messages', 
      icon: '💬',
      description: 'Chat with your connections'
    },
    { 
      name: 'Profile', 
      href: '/profile', 
      icon: '👤',
      description: 'Manage your profile information'
    },
    { 
      name: 'Settings', 
      href: '/account-settings', 
      icon: '⚙️',
      description: 'Account and privacy settings'
    },
  ];

  // Close sidebar when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSidebarOpen(false);
        setShowLogoutConfirm(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isActive = (href: string) => location.pathname === href;

  const filteredNavigation = navigation.filter(item => 
    !item.roleRequired || item.roleRequired === user?.role
  );

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUserInitials = () => {
    if (user?.profile?.firstName && user?.profile?.lastName) {
      return `${user.profile.firstName.charAt(0)}${user.profile.lastName.charAt(0)}`;
    }
    return user?.email.charAt(0).toUpperCase() || '?';
  };

  const getUserDisplayName = () => {
    if (user?.profile?.firstName) {
      return user.profile.firstName;
    }
    return user?.email.split('@')[0] || 'User';
  };

  // Mobile sidebar overlay
  const SidebarOverlay = () => (
    <div 
      className={`fixed inset-0 z-40 bg-gray-600 bg-opacity-75 transition-opacity duration-300 lg:hidden ${
        sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={() => setSidebarOpen(false)}
    />
  );

  // Logout confirmation modal
  const LogoutModal = () => (
    <div className={`fixed inset-0 z-50 overflow-y-auto ${showLogoutConfirm ? '' : 'hidden'}`}>
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setShowLogoutConfirm(false)} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">Confirm Logout</h3>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Are you sure you want to sign out? You'll need to sign in again to access your account.
          </p>
          <div className="flex space-x-3 justify-end">
            <button
              onClick={() => setShowLogoutConfirm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Navigation item component
  const NavItem = ({ item, isMobile = false }: { item: typeof navigation[0]; isMobile?: boolean }) => (
    <Link
      to={item.href}
      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
        isActive(item.href)
          ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      } ${isMobile ? 'mx-2' : ''}`}
      title={item.description}
    >
      <span className="mr-3 text-lg flex-shrink-0">{item.icon}</span>
      <div className="flex-1 min-w-0">
        <span className="truncate">{item.name}</span>
        {!isMobile && (
          <p className="text-xs text-gray-500 truncate mt-0.5 group-hover:text-gray-700">
            {item.description}
          </p>
        )}
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <SidebarOverlay />
      <LogoutModal />

      {/* Mobile header */}
      <div className="lg:hidden">
        <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6">
            <div className="flex justify-between items-center py-3">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 p-1"
                  aria-label="Open sidebar"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <h1 className="ml-3 text-xl font-bold text-blue-600">Tradrfy</h1>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Quick profile access */}
                <Link
                  to="/profile"
                  className="flex-shrink-0"
                >
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {getUserInitials()}
                    </span>
                  </div>
                </Link>
                
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="text-gray-500 hover:text-gray-700 p-1"
                  aria-label="Sign out"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:flex">
        {/* Desktop Sidebar */}
        <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 lg:bg-white lg:border-r lg:border-gray-200">
          <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0 px-4 mb-8">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white font-bold text-sm">T</span>
              </div>
              <h1 className="text-xl font-bold text-blue-600">Tradrfy</h1>
            </div>
            
            {/* Navigation */}
            <nav className="flex-1 px-2 space-y-2">
              {filteredNavigation.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
            </nav>

            {/* User Profile Section */}
            <div className="flex-shrink-0 px-4 py-4 border-t border-gray-200">
              <div className="flex items-center mb-3">
                <div className="flex-shrink-0">
                  {user?.profile?.avatarUrl ? (
                    <img
                      className="w-10 h-10 rounded-full object-cover"
                      src={user.profile.avatarUrl}
                      alt={`${getUserDisplayName()}'s avatar`}
                    />
                  ) : (
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {getUserInitials()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-xs text-gray-500 capitalize truncate">
                    {user?.role?.toLowerCase().replace('_', ' ')}
                  </p>
                  {user?.profile && (
                    <div className="flex items-center mt-1">
                      <div className={`w-2 h-2 rounded-full mr-1 ${
                        user.emailVerifiedAt ? 'bg-green-400' : 'bg-yellow-400'
                      }`} />
                      <span className="text-xs text-gray-500">
                        {user.emailVerifiedAt ? 'Verified' : 'Pending'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-1">
                <Link
                  to="/profile"
                  className="block w-full text-left text-xs text-gray-600 hover:text-gray-900 py-1 px-2 rounded hover:bg-gray-50"
                >
                  View Profile
                </Link>
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="block w-full text-left text-xs text-gray-600 hover:text-gray-900 py-1 px-2 rounded hover:bg-gray-50"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Sidebar */}
        <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white transform transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            {/* Mobile sidebar header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-sm">T</span>
                </div>
                <h1 className="text-xl font-bold text-blue-600">Tradrfy</h1>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-gray-500 hover:text-gray-700 p-1"
                aria-label="Close sidebar"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Mobile navigation */}
            <nav className="flex-1 px-2 py-4 space-y-2 overflow-y-auto">
              {filteredNavigation.map((item) => (
                <NavItem key={item.name} item={item} isMobile />
              ))}
            </nav>

            {/* Mobile user section */}
            <div className="border-t border-gray-200 p-4">
              <div className="flex items-center mb-3">
                <div className="flex-shrink-0">
                  {user?.profile?.avatarUrl ? (
                    <img
                      className="w-10 h-10 rounded-full object-cover"
                      src={user.profile.avatarUrl}
                      alt={`${getUserDisplayName()}'s avatar`}
                    />
                  ) : (
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {getUserInitials()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {user?.role?.toLowerCase().replace('_', ' ')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="w-full text-left text-sm text-red-600 hover:text-red-800 py-2 px-3 rounded hover:bg-red-50"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="lg:pl-64 flex flex-col flex-1">
          <main className="flex-1 relative">
            {children}
          </main>
        </div>
      </div>

      {/* Mobile bottom navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-pb">
        <nav className="flex">
          {filteredNavigation.slice(0, 5).map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`flex-1 flex flex-col items-center py-2 px-1 text-xs transition-colors duration-150 ${
                isActive(item.href)
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="text-lg mb-1">{item.icon}</span>
              <span className="truncate">{item.name}</span>
            </Link>
          ))}
        </nav>
      </div>

      {/* Bottom padding for mobile navigation */}
      <div className="lg:hidden h-16"></div>
    </div>
  );
};

export default Layout;